import { PartRequestStatus } from '@fsp-lib/shared/enums';
import {
  CustomTinyMceMenuItem,
  CustomTinyMceMenuItemsCollection,
  PartRemovedResponse,
  PartRequestResponse,
  TinyMceMenu,
} from '@fsp-lib/shared/models';

const defaultTinyMceMenu: TinyMceMenu = {
  file: { title: 'File', items: 'preview print' },
  edit: {
    title: 'Edit',
    items: 'undo redo cut copy paste pastetext selectall searchreplace',
  },
  view: { title: 'View', items: 'fullscreen' },
  insert: {
    title: 'Insert',
    items: 'link table charmap hr pagebreak insertdatetime',
  },
  tools: { title: 'Tools', items: 'wordcount' },
};

const defaultTinyMceToolbar = `
  undo redo | preview print | fullscreen | link table | formatselect | bold italic backcolor |
  alignleft aligncenter alignright alignjustify |
  bullist numlist outdent indent checklist | casechange removeformat`;

const tinyMceMenuWithCustomImage: TinyMceMenu = {
  file: defaultTinyMceMenu.file,
  edit: defaultTinyMceMenu.edit,
  view: defaultTinyMceMenu.view,
  insert: {
    title: defaultTinyMceMenu.insert?.title || '',
    items: `customImage ${defaultTinyMceMenu.insert?.items}`,
  },
  tools: defaultTinyMceMenu.tools,
};

const tinyMceMenuWithNoFontSizeOptions: TinyMceMenu = {
  edit: defaultTinyMceMenu.edit,
  file: defaultTinyMceMenu.file,
  format: {
    title: 'Format',
    items:
      // eslint-disable-next-line max-len
      'bold italic underline strikethrough superscript subscript codeformat | fontformats align | forecolor backcolor | removeformat',
  },
  insert: defaultTinyMceMenu.insert,
  tools: defaultTinyMceMenu.tools,
  view: defaultTinyMceMenu.view,
};

const wysiwygTinyMceMenu: TinyMceMenu = {
  edit: {
    title: 'Edit',
    items: 'undo redo cut copy paste pastetext selectall searchreplace',
  },
};

const wysiwygTinyMceToolbarNoLists = `
  undo redo | link | formatselect | bold italic | casechange removeformat`;

const wysiwygTinyMceToolbar = `
  undo redo | link | formatselect | bold italic |
  bullist numlist | casechange removeformat`;

export function renderWysiwygTinyMceMenu(): TinyMceMenu {
  return wysiwygTinyMceMenu;
}

export function renderWysiwygTinyMceToolbar(): string {
  return wysiwygTinyMceToolbar;
}

export function renderWysiwygTinyMceToolbarNoLists(): string {
  return wysiwygTinyMceToolbarNoLists;
}

export function renderDefaultTinyMceMenu(): TinyMceMenu {
  return defaultTinyMceMenu;
}

export function renderDefaultTinyToolbar(): string {
  return defaultTinyMceToolbar;
}

export function renderTinyMceMenuWithImage(): TinyMceMenu {
  return tinyMceMenuWithCustomImage;
}

export function renderTinyMceToolbarWithCustomImageButton(): string {
  return defaultTinyMceToolbar.replace('link', 'customImage link');
}

export function renderCertificateFormatsToolbar(): string {
  return defaultTinyMceToolbar
    .replace('link', 'customImage link')
    .replace('backcolor', 'forecolor backcolor');
}

export function renderTinyMceMenuWithNoFontSizeOptions(): TinyMceMenu {
  return tinyMceMenuWithNoFontSizeOptions;
}

export function renderTinyMceToolbarWithNoFontSizeOptions(): string {
  return defaultTinyMceToolbar.replace('formatselect | ', '');
}

export function renderPartsSnippetsMenu(
  partRequests: PartRequestResponse[],
  partsRemoved: PartRemovedResponse[],
): CustomTinyMceMenuItemsCollection {
  const added: CustomTinyMceMenuItem[] = [];
  const removed: CustomTinyMceMenuItem[] = [];
  partRequests?.forEach((partRequest, i) => {
    const { partDisplayNumber, partIsSerialized, serialNumbers, statusId } =
      partRequest;

    if (
      statusId === PartRequestStatus.picked ||
      statusId === PartRequestStatus.partialPicked
    ) {
      const partNumberText = `P/N: ${partDisplayNumber}`;

      if (partIsSerialized && serialNumbers) {
        serialNumbers.forEach((serialNumber) => {
          added.push({
            icon: 'plus',
            onAction: () => null,
            text: `${partNumberText} • S/N: ${serialNumber}`,
            type: 'menuitem',
            value: `added-${i}`,
          });
        });
      } else {
        added.push({
          icon: 'plus',
          onAction: () => null,
          text: partNumberText,
          type: 'menuitem',
          value: `added-${i}`,
        });
      }
    }
  });

  partsRemoved?.forEach((partRemoved, i) => {
    const { partIsSerialized, partNumber, serialNumber } = partRemoved;
    let text = `P/N: ${partNumber}`;

    if (partIsSerialized && serialNumber) {
      text += ` • S/N: ${serialNumber}`;
    }

    removed.push({
      icon: 'horizontal-rule',
      onAction: () => null,
      text,
      type: 'menuitem',
      value: `removed-${i}`,
    });
  });

  return {
    categories: [
      {
        getSubmenuItems: null,
        text: 'Added',
        customMenuItems: added,
        type: 'nestedmenuitem',
      },
      {
        getSubmenuItems: null,
        text: 'Removed',
        customMenuItems: removed,
        type: 'nestedmenuitem',
      },
    ],
    allMenuItemsInCollection: [...added, ...removed],
  };
}

export function extractFspImageIdsFromHtml(html: string | null): string[] {
  if (!html) {
    return [];
  }

  const imageIds: string[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const allImages = doc.querySelectorAll('img');

  // Extract GUIDs from certificate-format-images URLs
  // URL pattern: .../certificate-format-images/{guid}.{extension}
  const certFormatImagePattern = /\/certificate-format-images\/([a-f0-9-]+)\.\w+$/i;

  allImages.forEach((img) => {
    const src = img.getAttribute('src');
    if (src) {
      const match = src.match(certFormatImagePattern);
      if (match?.[1]) {
        imageIds.push(match[1]);
      }
    }
  });

  return imageIds;
}
